import React, { useEffect, useState } from "react";
import Modal from "../Components/Modal";
import NewInventoryForm from "./NewInventoryForm";
import {
  getAllDocsSnap,
  queryAllDocsByFieldSnap,
} from "../../../utils/firebase/Firebase.utils";
import ItemCard from "./ItemCard";
import PageModal from "../Components/PageModal";
import ItemDetailsPage from "./ItemDetailsPage";
import BentoTable from "../../../components/BentoTable";
import { slicedData } from "../../../utils/EleghoUtils";
import {
  FiBox,
  FiMap,
  FiUser,
  FiPackage,
  FiSearch,
  FiPlus,
  FiLayers,
  FiFilter,
  FiBarChart2,
  FiGrid,
  FiList,
  FiTrendingUp,
  FiTrendingDown,
} from "react-icons/fi";
import SelectInput from "../Components/SelectInput";

// Helper function to calculate balance from transactions
const calculateItemBalance = (transactions, itemId) => {
  let additions = 0;
  let issuances = 0;

  const itemTransactions = transactions.filter((t) => t.itemId === itemId);

  itemTransactions.forEach((transaction) => {
    if (transaction.transactionType === "addition") {
      additions += parseInt(transaction.quantity) || 0;
    } else if (transaction.transactionType === "issuance") {
      issuances += parseInt(transaction.quantity) || 0;
    }
  });

  return {
    additions,
    issuances,
    balance: additions - issuances,
  };
};

export default function Inventoryv2() {
  const [addMode, setAddMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [items, setItems] = useState([]);
  const [itemBalances, setItemBalances] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [numberOfItemPerPage, setNumberOfItemPerPage] = useState(5);
  const [viewType, setViewType] = useState("table"); // 'table' or 'grid'
  const [filters, setFilters] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  // Colors based on requirements
  const primaryColor = "#363062";
  const secondaryColor = "#8FCD00";

  // get departments from all inventories

  useEffect(() => {
    const fetchItems = async () => {
      await getAllDocsSnap("inventory", async (data) => {
        // Sort items by name
        data.sort((a, b) => {
          if (a.itemName < b.itemName) {
            return -1;
          }
          if (a.itemName > b.itemName) {
            return 1;
          }
          return 0;
        });

        const allDepartments = data.map((d) => {
          return d?.department.toLowerCase();
        });

        const newArrayDepartments = [...new Set(allDepartments)];
        setFilters(["all", ...newArrayDepartments]);

        setItems(data || []);

        // Initialize empty balances object
        const balances = {};

        // Fetch all transactions
        await getAllDocsSnap("inventoryTransactions", (transactions) => {
          if (transactions && transactions.length > 0) {
            // For each item, calculate its balance using the helper function
            for (const item of data) {
              balances[item.id] = calculateItemBalance(transactions, item.id);
            }
          } else {
            // If no transactions exist, initialize all items with zero balances
            for (const item of data) {
              balances[item.id] = {
                additions: 0,
                issuances: 0,
                balance: 0,
              };
            }
          }

          setItemBalances(balances);
          setIsLoading(false);
        });
      });
    };

    fetchItems();
  }, []);

  const handleAddMode = () => {
    setAddMode(!addMode);
  };

  const handleSelect = (selected) => {
    setSelectedItem({
      ...selected,
      // Add balance data to the selected item before passing to ItemDetailsPage
      balanceData: itemBalances[selected.id] || {
        additions: 0,
        issuances: 0,
        balance: 0,
      },
    });
    setViewMode(!viewMode);
  };

  const departmentSpecificItems = items.filter((item) => {
    if (activeFilter === "all") {
      return item;
    } else {
      return item.department === activeFilter;
    }
  });

  // Filter items based on search term
  const filteredItems = departmentSpecificItems.filter(
    (item) =>
      item.itemName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      item.location?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      item.custodian?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  // Get current items for pagination
  const currentItems = filteredItems.slice(
    (currentPage - 1) * numberOfItemPerPage,
    currentPage * numberOfItemPerPage
  );

  // Calculate inventory statistics
  const stats = {
    totalItems: items.length,
    uniqueLocations: [...new Set(items.map((item) => item.location))].length,
    uniqueCustodians: [...new Set(items.map((item) => item.custodian))].length,
    totalQuantity: Object.values(itemBalances).reduce(
      (sum, balance) => sum + (balance.balance || 0),
      0
    ),
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-b-2 mb-4"
            style={{ borderColor: primaryColor }}
          ></div>
          <div className="text-sm">Loading inventory data...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-gray-600 text-sm text-left max-w-7xl mx-auto mt-8">
      <div className="font-bold text-lg mb-2" style={{ color: primaryColor }}>
        Inventory Management
      </div>
      <p className="text-sm text-gray-500 mb-6">
        Manage and track your inventory items across all locations
      </p>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 my-6">
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: `${primaryColor}10` }}
            >
              <FiBox style={{ color: primaryColor }} className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Items</div>
              <div
                className="text-lg font-semibold"
                style={{ color: primaryColor }}
              >
                {stats.totalItems}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: `${secondaryColor}10` }}
            >
              <FiPackage
                style={{ color: secondaryColor }}
                className="w-5 h-5"
              />
            </div>
            <div>
              <div className="text-sm text-gray-600">Total Quantity</div>
              <div
                className="text-lg font-semibold"
                style={{ color: secondaryColor }}
              >
                {stats.totalQuantity}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: `${primaryColor}10` }}
            >
              <FiMap style={{ color: primaryColor }} className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Locations</div>
              <div
                className="text-lg font-semibold"
                style={{ color: primaryColor }}
              >
                {stats.uniqueLocations}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3">
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: `${secondaryColor}10` }}
            >
              <FiUser style={{ color: secondaryColor }} className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Custodians</div>
              <div
                className="text-lg font-semibold"
                style={{ color: secondaryColor }}
              >
                {stats.uniqueCustodians}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* add mode and search bar plus the cards and list button */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <button
          onClick={handleAddMode}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{ backgroundColor: secondaryColor, color: "white" }}
        >
          <FiPlus className="w-4 h-4" />
          <span>Add New Item</span>
        </button>

        <div className="flex items-center gap-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search items..."
              className="border border-gray-200 rounded-lg px-4 py-2 pl-9 focus:outline-none focus:ring-2 text-sm w-full md:w-auto"
              style={{ focusRing: `${primaryColor}40` }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>

          <div className="flex bg-gray-100 rounded-md p-1">
            <button
              className={`p-2 rounded-md ${
                viewType === "table" ? "text-white" : "text-gray-500"
              }`}
              style={{
                backgroundColor:
                  viewType === "table" ? primaryColor : "transparent",
              }}
              onClick={() => setViewType("table")}
            >
              <FiList className="w-4 h-4" />
            </button>
            <button
              className={`p-2 rounded-md ${
                viewType === "grid" ? "text-white" : "text-gray-500"
              }`}
              style={{
                backgroundColor:
                  viewType === "grid" ? primaryColor : "transparent",
              }}
              onClick={() => setViewType("grid")}
            >
              <FiGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* filter inventory per department */}
      <div className="flex items-center gap-4 mb-4">
        {filters.map((dept) => (
          <div
            onClick={() => setActiveFilter(dept)}
            className={`capitalize ${
              activeFilter === dept ? "active-pill" : "inactive-pill"
            }`}
          >
            {dept}
          </div>
        ))}
      </div>

      {/* table view */}
      {viewType === "table" ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <div
              className="font-medium flex items-center gap-2"
              style={{ color: primaryColor }}
            >
              <FiLayers className="w-4 h-4" />
              <span>All Inventory Items</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <FiFilter className="w-4 h-4" />
              <span>
                Showing {currentItems.length} of {filteredItems.length} items
              </span>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {/* Table Header */}
            <div
              className="flex items-center gap-4 p-4 font-medium text-gray-700 text-xs"
              style={{ backgroundColor: `${primaryColor}05` }}
            >
              <div className="w-20">Status</div>
              <div className="w-1/6">Item Name</div>
              <div className="w-1/6">Location</div>
              <div className="w-1/6">Department</div>
              <div className="w-1/6">Quantity</div>
              <div className="w-1/6">Balance</div>
              <div className="w-1/6 text-right">Actions</div>
            </div>

            {currentItems.length > 0 ? (
              currentItems.map((item, index) => (
                <div
                  key={index}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleSelect(item)}
                >
                  <ItemCardEnhanced
                    data={item}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    balance={itemBalances[item.id]?.balance || 0}
                  />
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500">
                <FiPackage className="w-10 h-10 mx-auto mb-2 opacity-20" />
                <p>No inventory items found</p>
                <p className="text-xs mt-1">
                  Try adjusting your search criteria
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-gray-100 flex justify-between items-center">
            <div>
              <select
                className="border border-gray-200 rounded p-1 text-xs"
                value={numberOfItemPerPage}
                onChange={(e) => {
                  setNumberOfItemPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            <div className="flex gap-2">
              <button
                className="px-3 py-1 rounded border border-gray-200 text-xs disabled:opacity-50"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
              <div className="px-3 py-1 rounded text-xs bg-gray-50">
                Page {currentPage} of{" "}
                {Math.max(
                  1,
                  Math.ceil(filteredItems.length / numberOfItemPerPage)
                )}
              </div>
              <button
                className="px-3 py-1 rounded border border-gray-200 text-xs disabled:opacity-50"
                disabled={
                  currentPage >=
                  Math.ceil(filteredItems.length / numberOfItemPerPage)
                }
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(
                      prev + 1,
                      Math.ceil(filteredItems.length / numberOfItemPerPage)
                    )
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <div
                key={index}
                className="bg-white p-5 rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200 cursor-pointer"
                onClick={() => handleSelect(item)}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <div
                      className="p-2 rounded-md mr-3"
                      style={{ backgroundColor: `${primaryColor}10` }}
                    >
                      <FiPackage size={18} style={{ color: primaryColor }} />
                    </div>
                    <h3 className="font-medium text-gray-800">
                      {item.itemName}
                    </h3>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="text-sm text-gray-500 flex items-center gap-1 mb-1">
                    <span className="text-xs uppercase">Category:</span>{" "}
                    {item.category || "N/A"}
                  </div>
                  <div className="flex items-center gap-1 mb-1">
                    <FiMap size={12} className="text-gray-400" />
                    <span className="text-sm text-gray-600">
                      {item.location || "Not specified"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mb-1">
                    <FiUser size={12} className="text-gray-400" />
                    <span className="text-sm text-gray-600 capitalize">
                      {item.department.toLowerCase() || "Not assigned"}
                    </span>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">INITIAL QTY</div>
                    <div
                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md"
                      style={{
                        backgroundColor:
                          parseInt(item.quantity) > 30
                            ? `${secondaryColor}15`
                            : parseInt(item.quantity) > 10
                            ? "rgba(251, 191, 36, 0.15)"
                            : "rgba(239, 68, 68, 0.15)",
                        color:
                          parseInt(item.quantity) > 30
                            ? secondaryColor
                            : parseInt(item.quantity) > 10
                            ? "#F59E0B"
                            : "#EF4444",
                      }}
                    >
                      {parseInt(item.quantity) || 0} units
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">CURRENT BALANCE</div>
                    <div className="flex items-center gap-1">
                      <div
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md"
                        style={{
                          backgroundColor:
                            (itemBalances[item.id]?.balance || 0) > 30
                              ? `${secondaryColor}15`
                              : (itemBalances[item.id]?.balance || 0) > 10
                              ? "rgba(251, 191, 36, 0.15)"
                              : "rgba(239, 68, 68, 0.15)",
                          color:
                            (itemBalances[item.id]?.balance || 0) > 30
                              ? secondaryColor
                              : (itemBalances[item.id]?.balance || 0) > 10
                              ? "#F59E0B"
                              : "#EF4444",
                        }}
                      >
                        {itemBalances[item.id]?.balance || 0} units
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-100">
                  <div
                    className="text-xs py-1 px-2 rounded-full"
                    style={{
                      backgroundColor:
                        item.status === "Available"
                          ? `${secondaryColor}15`
                          : `${primaryColor}15`,
                      color:
                        item.status === "Available"
                          ? secondaryColor
                          : primaryColor,
                    }}
                  >
                    {item.status || "Unknown"}
                  </div>

                  <button
                    className="text-xs py-1 px-3 rounded-md border"
                    style={{ borderColor: primaryColor, color: primaryColor }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full bg-white p-8 rounded-lg shadow-sm text-center border border-gray-100">
              <div className="flex flex-col items-center justify-center">
                <div className="p-4 rounded-full bg-gray-100 mb-4">
                  <FiPackage size={24} className="text-gray-400" />
                </div>
                <h3 className="text-gray-800 font-medium mb-2">
                  No inventory items found
                </h3>
                <p className="text-gray-500 mb-4">
                  Try adjusting your search criteria
                </p>
                <button
                  className="px-4 py-2 rounded-md text-white font-medium flex items-center gap-2"
                  style={{ backgroundColor: secondaryColor }}
                  onClick={() => setSearchTerm("")}
                >
                  <FiFilter size={16} /> Clear Search
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {addMode && (
        <Modal>
          <NewInventoryForm setAddMode={setAddMode} />
        </Modal>
      )}

      {viewMode && (
        <PageModal>
          <ItemDetailsPage data={selectedItem} setViewMode={setViewMode} />
        </PageModal>
      )}
    </div>
  );
}

// Enhanced ItemCard component with better quantity display
function ItemCardEnhanced({ data, primaryColor, secondaryColor, balance }) {
  return (
    <div className="flex items-center gap-4 p-4">
      <div className="w-20">
        <span
          className="text-xs py-1 px-2 rounded-full whitespace-nowrap"
          style={{
            backgroundColor:
              data.status === "Available"
                ? `${secondaryColor}15`
                : `${primaryColor}15`,
            color: data.status === "Available" ? secondaryColor : primaryColor,
          }}
        >
          {data.status || "Unknown"}
        </span>
      </div>
      <div className="w-1/6 font-medium text-gray-800">{data.itemName}</div>
      <div className="w-1/6 text-gray-600 flex items-center gap-1">
        <FiMap size={12} className="text-gray-400" />
        <span>{data.location || "Not specified"}</span>
      </div>
      <div className="w-1/6 text-gray-600 flex items-center gap-1">
        <FiUser size={12} className="text-gray-400 capitalize" />
        <span>{data.department.toLowerCase() || "Not assigned"}</span>
      </div>
      <div className="w-1/6">
        <div
          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md"
          style={{
            backgroundColor:
              parseInt(data.quantity) > 30
                ? `${secondaryColor}15`
                : parseInt(data.quantity) > 10
                ? "rgba(251, 191, 36, 0.15)"
                : "rgba(239, 68, 68, 0.15)",
            color:
              parseInt(data.quantity) > 30
                ? secondaryColor
                : parseInt(data.quantity) > 10
                ? "#F59E0B"
                : "#EF4444",
          }}
        >
          {data.quantity || 0} units
        </div>
      </div>
      <div className="w-1/6">
        <div
          className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md"
          style={{
            backgroundColor:
              balance > 30
                ? `${secondaryColor}15`
                : balance > 10
                ? "rgba(251, 191, 36, 0.15)"
                : "rgba(239, 68, 68, 0.15)",
            color:
              balance > 30
                ? secondaryColor
                : balance > 10
                ? "#F59E0B"
                : "#EF4444",
          }}
        >
          <div className="flex items-center gap-1">
            {balance >= 0 ? (
              <FiTrendingUp size={10} />
            ) : (
              <FiTrendingDown size={10} />
            )}
            {balance} units
          </div>
        </div>
      </div>
      <div className="w-1/6 text-right">
        <button
          className="text-xs py-1 px-3 rounded-md border"
          style={{ borderColor: primaryColor, color: primaryColor }}
        >
          View Details
        </button>
      </div>
    </div>
  );
}
